/**
 * PWA Install Prompt Component
 *
 * Shows a banner prompting users to install the app on their device.
 * Handles both Android (beforeinstallprompt) and iOS (manual instructions).
 */

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Share, Plus } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Detect iOS
const isIOS = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // navigator.platform is deprecated but remains the only reliable signal for
    // iPadOS, which reports a desktop Safari UA. No standardised replacement.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

// Detect if already installed as PWA
const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
};

export function InstallPrompt() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Suppress the banner while in an active conversation - it would cover the chat input
  const isActiveConversation = /^\/chats\/.+/.test(location.pathname);

  useEffect(() => {
    // Check if already dismissed or installed
    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    if (wasDismissed || isStandalone()) {
      return;
    }

    // Handle Android/Chrome install prompt. Delay long enough that the user
    // has settled into the app before we nag - the prompt is a 150px-tall
    // banner that blocks the chats list at the top on mobile, so popping
    // it up immediately on first arrival was making /chats look broken.
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setTimeout(() => { setShowPrompt(true); }, 30_000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show after a longer delay if not installed.
    if (isIOS() && !isStandalone()) {
      setTimeout(() => { setShowPrompt(true); }, 30_000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      // iOS - show instructions
      setShowIOSInstructions(true);
      return;
    }

    // Android/Chrome
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    // Remember dismissal for 7 days
    const expiry = Date.now() + 7 * 24 * 60 * 60 * 1000;
    localStorage.setItem('pwa-install-dismissed', expiry.toString());
  };

  const handleDismissForever = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', 'forever');
  };

  if (dismissed || !showPrompt || isStandalone() || isActiveConversation) {
    return null;
  }

  return (
    <AnimatePresence>
      {showPrompt && (
        <>
          {/* Main Install Banner */}
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 80 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            // Mobile: dock above the bottom-nav (h-16 + safe-area) so it
            // doesn't cover the chat list at the top. Desktop: bottom-right
            // unchanged. Previously `top-4 left-4 right-4` on mobile blocked
            // the entire visible chats list when the prompt was up.
            className="fixed left-4 right-4 z-50 bottom-[calc(72px+env(safe-area-inset-bottom))] md:bottom-4 md:left-auto md:right-4 md:w-96"
          >
            <div className="rounded-2xl bg-gradient-to-br from-surface-2 to-surface-1 border border-line-2 p-4 shadow-2xl">
              <div className="flex items-start gap-4">
                {/* App Icon */}
                <div className="flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden shadow-lg">
                  <img src="/chlann-icon.svg" alt="Chlann" className="w-full h-full" draggable={false} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-ink-1">
                    Install Chlann
                  </h3>
                  <p className="text-sm text-ink-3 mt-1">
                    Add to your home screen for the best experience
                  </p>
                </div>

                {/* Close button — min 44px touch target */}
                <button
                  onClick={handleDismiss}
                  className="flex-shrink-0 p-2.5 text-ink-3 hover:text-ink-1 transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleInstall}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-ink-1 font-medium rounded-xl transition-colors"
                >
                  <Download className="w-5 h-5" />
                  Install App
                </button>
                <button
                  onClick={handleDismissForever}
                  className="px-4 py-3 text-ink-3 hover:text-ink-1 font-medium rounded-xl transition-colors"
                >
                  Not now
                </button>
              </div>
            </div>
          </motion.div>

          {/* iOS Instructions Modal */}
          <AnimatePresence>
            {showIOSInstructions && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm"
                onClick={() => { setShowIOSInstructions(false); }}
              >
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                  className="w-full max-w-lg rounded-t-3xl bg-surface-2 p-6 pb-10"
                  onClick={(e) => { e.stopPropagation(); }}
                >
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-1.5 rounded-full bg-surface-3" />
                  </div>

                  <h3 className="text-xl font-bold text-ink-1 text-center mb-6">
                    Install Chlann on iOS
                  </h3>

                  <div className="space-y-6">
                    {/* Step 1 */}
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <span className="text-emerald-400 font-bold">1</span>
                      </div>
                      <div>
                        <p className="text-ink-1 font-medium">
                          Tap the Share button
                        </p>
                        <p className="text-sm text-ink-3 mt-1 flex items-center gap-2">
                          <Share className="w-4 h-4" />
                          at the bottom of Safari
                        </p>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <span className="text-emerald-400 font-bold">2</span>
                      </div>
                      <div>
                        <p className="text-ink-1 font-medium">
                          Scroll and tap &quot;Add to Home Screen&quot;
                        </p>
                        <p className="text-sm text-ink-3 mt-1 flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          in the share menu
                        </p>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <span className="text-emerald-400 font-bold">3</span>
                      </div>
                      <div>
                        <p className="text-ink-1 font-medium">
                          Tap &quot;Add&quot; to install
                        </p>
                        <p className="text-sm text-ink-3 mt-1">
                          Chlann will appear on your home screen
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setShowIOSInstructions(false);
                      handleDismiss();
                    }}
                    className="w-full mt-8 py-4 bg-surface-3 hover:bg-surface-sunken text-ink-1 font-medium rounded-xl transition-colors"
                  >
                    Got it
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}

export default InstallPrompt;
